import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { FAILURE_ORDERS, getNextOrders, getOrders } from '@actions/order'
import InfiniteScroll from '@shared/InfiniteScroll'
import { orderStatus } from '@utils/orderStatus'

import V2AccountLayout from '../V2AccountLayout'
import V2StatusPill from '../V2StatusPill'
import carritoSuporte from '../assets/carrito-suporte.png'

// Lista de pedidos do comprador no V2 — porte fiel do design "Gestão de Pedidos".
// Regras preservadas (MATRIZ MC-10..MC-15): mesma fonte getOrders({id}), mesma
// paginação por InfiniteScroll, status pelo MESMO mapa orderStatus, 2ª via de
// boleto só com billet_url + "Aguardando pagamento". Os controles inertes do V1
// (MC-14) não foram portados.

export default function V2OrderList() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user = useSelector(state => (state.sessionReducer && state.sessionReducer.user) || {})
  const order = useSelector(state => state.order || {})
  // o reducer V1 não guarda isFetching — controle local para o empty state não
  // piscar "Nenhum pedido" enquanto a primeira página carrega
  const [loaded, setLoaded] = React.useState(false)

  useEffect(() => {
    if (!user.id) return
    let cancelled = false
    // achado #17: logo após login/registro o token pode ainda não estar persistido
    // e o 1º fetch falha (a API devolve 500 para JWT malformado) — um retry único
    // após a persistência cobre a corrida sem mudar nada no V1
    dispatch(getOrders({ id: user.id }))
      .then(action => {
        if (!cancelled && action && action.type === FAILURE_ORDERS) {
          return new Promise(r => setTimeout(r, 1500)).then(() => !cancelled && dispatch(getOrders({ id: user.id })))
        }
      })
      .finally(() => !cancelled && setLoaded(true))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id])

  const orders = order.orders || []

  return (
    <V2AccountLayout>
      <div className="v2-acc-head">
        <div>
          <h1 style={{ margin: 0, fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '30px', lineHeight: '1.1', letterSpacing: '-0.6px', color: '#373535' }}>
            Meus pedidos
          </h1>
          <p style={{ margin: '8px 0 0', fontSize: '14px', lineHeight: '1.5', color: '#6B6664' }}>
            Acompanhe o andamento, baixe boletos e veja os detalhes de cada compra.
          </p>
        </div>
        <button type="button" id="v2-acc-newbuy" className="v2-btn v2-btn--primary v2-btn--sm" onClick={() => navigate('/')}>
          Nova compra com voucher
        </button>
      </div>

      <InfiniteScroll
        loadMore={+order.page < +order.total_pages}
        action={() => dispatch(getNextOrders({ id: user.id, page: +order.page + 1 }))}
      >
        <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {orders.map((o, index) => {
            const st = orderStatus[o.order_status ?? o.status] || orderStatus['null']
            const awaiting = st.description === 'Aguardando pagamento'
            return (
              <section
                key={`${o.order_id}-${index}`}
                className="v2-acc-ordercard"
                style={{ background: '#fff', border: '1px solid #EFE7E3', borderRadius: '12px', padding: '18px 22px', display: 'flex', alignItems: 'center', gap: '20px' }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '16px', color: '#373535' }}>Pedido SMD-{o.order_id}</span>
                    <V2StatusPill {...st} code={o.order_status ?? o.status} />
                  </div>
                  <span style={{ display: 'block', marginTop: '6px', fontSize: '13px', color: '#6B6664' }}>Realizado em {o.created}</span>
                </div>
                <div className="v2-acc-ordercard__side" style={{ flex: 'none', textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                  <span style={{ fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '18px', color: '#373535' }}>{o.total}</span>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {o.billet_url && awaiting && (
                      <button
                        type="button"
                        onClick={() => window.open(o.billet_url)}
                        style={{ height: '36px', padding: '0 14px', border: '1px solid #E3DBD7', borderRadius: '8px', background: '#fff', fontSize: '12.5px', fontWeight: '700', color: '#6B6664', cursor: 'pointer' }}
                      >
                        2ª via de boleto
                      </button>
                    )}
                    <button
                      type="button"
                      className="v2-acc-details-btn"
                      onClick={() => navigate(`/v2/conta/pedidos/${o.order_id}`)}
                      style={{ height: '36px', padding: '0 16px', border: 'none', borderRadius: '8px', background: '#D8262C', color: '#fff', fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}
                    >
                      Ver detalhes
                    </button>
                  </div>
                </div>
              </section>
            )
          })}
          {loaded && !orders.length && (
            <div style={{ background: '#fff', border: '1px solid #EFE7E3', borderRadius: '12px', padding: '28px', textAlign: 'center', color: '#6B6664', fontSize: '14px' }}>
              Nenhum pedido encontrado.
            </div>
          )}
        </div>
      </InfiniteScroll>

      <div className="v2-acc-help">
        <img src={carritoSuporte} alt="" aria-hidden="true" />
        <div>
          <strong>Algum problema com um pedido?</strong>
          <p>Troca, cancelamento ou dúvida sobre a entrega: abra um atendimento que o nosso time responde pelo e-mail cadastrado.</p>
        </div>
        <button type="button" className="v2-btn v2-btn--primary v2-btn--sm" onClick={() => navigate('/v2/conta/atendimento')}>
          Abrir atendimento
        </button>
      </div>
    </V2AccountLayout>
  )
}
